import { getIconComponent } from '../services/iconService';

// Static fallback vehicle types (used when API is unavailable)
const STATIC_VEHICLE_TYPES = {
    KTW: {
        value: 'KTW',
        label: 'Krankentransportwagen',
        shortLabel: 'KTW',
        description: 'Professioneller Krankentransportwagen für medizinische Transporte',
        icon: 'LocalShipping',
        color: '#2196F3'
    },
    TAXI: {
        value: 'TAXI',
        label: 'Taxi',
        shortLabel: 'Taxi',
        description: 'Lizenziertes Taxi für Krankenfahrten',
        icon: 'LocalTaxi',
        color: '#FFC107'
    },
    MIETWAGEN: {
        value: 'MIETWAGEN',
        label: 'Mietwagen',
        shortLabel: 'Mietwagen',
        description: 'Mietwagen mit Fahrer für Krankenfahrten',
        icon: 'DirectionsCar',
        color: '#9C27B0'
    },
    ROLLSTUHLGERECHTES_FAHRZEUG: {
        value: 'ROLLSTUHLGERECHTES_FAHRZEUG',
        label: 'Rollstuhlgerechtes Fahrzeug',
        shortLabel: 'Rollstuhl',
        description: 'Speziell ausgestattetes Fahrzeug für Rollstuhlfahrer',
        icon: 'Accessible',
        color: '#4CAF50'
    },
    PRIVATES_KFZ: {
        value: 'PRIVATES_KFZ',
        label: 'Privates KFZ',
        shortLabel: 'Privat-KFZ',
        description: 'Privates Fahrzeug (z.B. für Angehörigenfahrten)',
        icon: 'DriveEta',
        color: '#607D8B'
    }
};

// Dynamic vehicle types cache
let dynamicVehicleTypes = null;
let lastFetchTime = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Clear the vehicle types cache
 */
export const clearVehicleTypesCache = () => {
    dynamicVehicleTypes = null;
    lastFetchTime = null;
};

/**
 * Load vehicle types from API
 * @param {string} apiKey - API key for authentication
 * @returns {Promise<Object>} Vehicle types object
 */
export const loadVehicleTypes = async (apiKey) => {
    // Check cache
    if (dynamicVehicleTypes && lastFetchTime && Date.now() - lastFetchTime < CACHE_DURATION) {
        return dynamicVehicleTypes;
    }

    try {
        const response = await fetch(`${process.env.REACT_APP_API_URI}/api/vehicle-categories`, {
            headers: {
                'Authorization': apiKey
            }
        });

        if (!response.ok) {
            throw new Error('Failed to fetch vehicle categories');
        }

        const categories = await response.json();

        // Convert API response to VEHICLE_TYPES format
        // Sort by sort_order
        const sortedCategories = categories
            .sort((a, b) => a.sort_order - b.sort_order);

        dynamicVehicleTypes = sortedCategories.reduce((acc, cat) => {
            acc[cat.category_code] = {
                value: cat.category_code,
                label: cat.label,
                shortLabel: cat.short_label,
                description: cat.description,
                icon: cat.icon_name,
                color: cat.color_hex,
                properties: cat.properties || {}
            };
            return acc;
        }, {});

        lastFetchTime = Date.now();
        return dynamicVehicleTypes;
    } catch (error) {
        console.error('Error loading vehicle types from API:', error);
        // Fallback to static types
        return STATIC_VEHICLE_TYPES;
    }
};

/**
 * Get current vehicle types (from cache or static)
 * @returns {Object} Vehicle types object
 */
export const getVehicleTypes = () => {
    return dynamicVehicleTypes || STATIC_VEHICLE_TYPES;
};

/**
 * Get vehicle types with icon components
 * @returns {Object} Vehicle types with resolved icon components
 */
export const getVehicleTypesWithIcons = () => {
    const types = getVehicleTypes();
    const typesWithIcons = {};

    Object.keys(types).forEach(key => {
        typesWithIcons[key] = {
            ...types[key],
            icon: getIconComponent(types[key].icon)
        };
    });

    return typesWithIcons;
};

// Export for backward compatibility - this will initially return static types
// Components should use loadVehicleTypes() or getVehicleTypesWithIcons() for dynamic types
export const VEHICLE_TYPES = STATIC_VEHICLE_TYPES;

// Array of vehicle type values for validation
export const getVehicleTypeValues = () => {
    return Object.keys(getVehicleTypes());
};

// Get vehicle type info by value
export const getVehicleTypeInfo = (vehicleType) => {
    const types = getVehicleTypesWithIcons();
    return types[vehicleType] || types.KTW || Object.values(types)[0];
};

// Helper functions based on vehicle category properties
export const isMedicalMaterialTransportVehicle = (vehicleCategory) => {
    return vehicleCategory?.properties?.medical_material_transport === true;
};

export const isQualifiedTransportVehicle = (vehicleCategory) => {
    return vehicleCategory?.properties?.qualified_transport === true;
};

export const isUnqualifiedTransportVehicle = (vehicleCategory) => {
    return vehicleCategory?.properties?.unqualified_transport === true;
};

// Check if a vehicle is suitable for a transport
export const isVehicleSuitableForTransport = (vehicle, transport) => {
    const categoryProps = vehicle.vehicle_category_properties || {};

    switch (transport.transport_category) {
        case 'QUALIFIED_KTW':
            return categoryProps.qualified_transport === true;

        case 'UNQUALIFIED_KRANKENFAHRT':
            return categoryProps.unqualified_transport === true;

        case 'MEDICAL_MATERIAL_TRANSPORT':
            if (categoryProps.medical_material_transport !== true) return false;

            // Zusätzliche Prüfung der Fahrzeugausstattung
            const vehicleProps = vehicle.vehicle_properties || {};
            const details = transport.medical_material_details;

            // Temperatur-Check
            if (details?.temperature_requirement && details.temperature_requirement !== 'ROOM_TEMP') {
                const tempCapabilities = vehicleProps.temperature_control_capability || [];
                const requiredTemp = mapTemperatureRequirement(details.temperature_requirement);
                if (!tempCapabilities.includes(requiredTemp)) return false;
            }

            // Biohazard-Check basierend auf benutzerdefinierten Anforderungen
            if (details?.biohazard_required) {
                if (!vehicleProps.biohazard_certified) return false;
            }

            return true;

        default:
            return true; // Abwärtskompatibilität
    }
};

const mapTemperatureRequirement = (requirement) => {
    const mapping = {
        'COOLED_2_8': '2-8°C',
        'FROZEN_MINUS_20': '-20°C',
        'FROZEN_MINUS_80': '-80°C',
        'LIQUID_NITROGEN': '-196°C'
    };
    return mapping[requirement] || requirement;
};

// Legacy functions for backward compatibility
export const isUnqualifiedTransportVehicleType = (vehicleType) => {
    console.warn('isUnqualifiedTransportVehicleType is deprecated. Use isUnqualifiedTransportVehicle with vehicle category object instead.');
    // For backward compatibility, check static types
    return vehicleType !== 'KTW';
};

export const getUnqualifiedTransportVehicleTypes = () => {
    console.warn('getUnqualifiedTransportVehicleTypes is deprecated. Filter vehicle categories by properties.unqualified_transport instead.');
    const types = getVehicleTypesWithIcons();
    const unqualifiedTypes = {};
    
    Object.keys(types).forEach(key => {
        if (key !== 'KTW') {
            unqualifiedTypes[key] = types[key];
        }
    });
    
    return unqualifiedTypes;
};

export const getMedicalMaterialTransportVehicleTypes = () => {
    console.warn('getMedicalMaterialTransportVehicleTypes is deprecated. Filter vehicle categories by properties.medical_material_transport instead.');
    const types = getVehicleTypesWithIcons();
    const medicalTypes = {};
    
    Object.keys(types).forEach(key => {
        if (['MEDICAL_COURIER', 'BLOOD_TRANSPORT', 'ORGAN_TRANSPORT'].includes(key)) {
            medicalTypes[key] = types[key];
        }
    });
    
    return medicalTypes;
};

// VEHICLE_PROPERTIES_CONFIG removed - now handled dynamically in VehicleEquipmentEditor
// based on vehicle category properties